import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TransactionHistory = ({ transactions }) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    })?.format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })?.format(date);
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'purchase':
        return 'ShoppingBag';
      case 'subsidy':
        return 'Gift';
      case 'refund':
        return 'RotateCcw';
      case 'bonus':
        return 'Star';
      default:
        return 'Circle';
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'purchase':
        return 'var(--color-error)';
      case 'subsidy':
        return 'var(--color-success)';
      case 'refund':
        return 'var(--color-success)';
      case 'bonus':
        return 'var(--color-accent)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const filteredTransactions = transactions?.filter(transaction => {
    if (filter === 'all') return true;
    return transaction?.type === filter;
  });

  const sortedTransactions = [...filteredTransactions]?.sort((a, b) => {
    if (sortBy === 'date') {
      return new Date(b.date) - new Date(a.date);
    }
    if (sortBy === 'amount') {
      return Math.abs(b?.amount) - Math.abs(a?.amount);
    }
    return 0;
  });

  const filterOptions = [
    { value: 'all', label: 'Todas' },
    { value: 'purchase', label: 'Compras' },
    { value: 'subsidy', label: 'Subsidios' },
    { value: 'refund', label: 'Reembolsos' },
    { value: 'bonus', label: 'Bonificaciones' }
  ];

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-semibold text-lg text-foreground">
          Historial de Transacciones
        </h3>
        <div className="flex items-center space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e?.target?.value)}
            className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground"
          >
            {filterOptions?.map(option => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-1.5 text-sm border border-border rounded-md bg-background text-foreground"
          >
            <option value="date">Por Fecha</option>
            <option value="amount">Por Monto</option>
          </select>
        </div>
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto">
        {sortedTransactions?.length > 0 ? (
          sortedTransactions?.map((transaction) => (
            <div
              key={transaction?.id}
              className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth"
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center">
                  <Icon
                    name={getTransactionIcon(transaction?.type)}
                    size={20}
                    color={getTransactionColor(transaction?.type)}
                  />
                </div>
                <div className="flex-1">
                  <h4 className="font-body font-medium text-foreground">
                    {transaction?.description}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(transaction?.date)}
                  </p>
                  {transaction?.business && (
                    <p className="text-xs text-muted-foreground">
                      {transaction?.business}
                    </p>
                  )}
                </div>
              </div>

              <div className="text-right">
                <p className={`font-data font-semibold ${
                  transaction?.amount > 0 ? 'text-success' : 'text-error'
                }`}>
                  {transaction?.amount > 0 ? '+' : ''}{formatCurrency(transaction?.amount)}
                </p>
                {transaction?.savings && (
                  <p className="text-xs text-success">
                    Ahorro: {formatCurrency(transaction?.savings)}
                  </p>
                )}
                {transaction?.impact && (
                  <div className="flex items-center space-x-1 mt-1">
                    <Icon name="Leaf" size={12} color="var(--color-success)" />
                    <span className="text-xs text-success">
                      {transaction?.impact?.co2}kg CO₂
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8">
            <Icon name="Receipt" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-muted-foreground">
              No hay transacciones para mostrar
            </p>
          </div>
        )}
      </div>
      {sortedTransactions?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <Button variant="outline" className="w-full">
            <Icon name="Download" size={16} className="mr-2" />
            Descargar Historial Completo
          </Button>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;